<script setup lang="ts">
import DeviceMeta from '../DeviceMeta';
import { computed } from 'vue';
import type { PropType } from 'vue';
import type { Value } from '../Default/lib';
import type { MetaProps } from '../DeviceMeta/lib';

const props = defineProps({
  leak: {
    type: Object as PropType<Value>,
    default: undefined,
  },
  temp: {
    type: Object as PropType<Value>,
    default: undefined,
  },
  humidity: {
    type: Object as PropType<Value>,
    default: undefined,
  },
  meta: {
    type: Object as PropType<MetaProps>,
    required: true,
  },
});

const leakDetection = computed(() => props.leak ? props.leak.value : undefined);
const temp = computed(() => props.temp);
const humidity = computed(() => props.humidity);
</script>

<template>
  <div class="w-full text-white font-light font-sans relative">
    <div class="flex items-center justify-center">
      <div class="h-32 w-32">
        <svg
          width="121px"
          height="108px"
          viewBox="0 0 121 108"
          version="1.1"
          xmlns="http://www.w3.org/2000/svg"
          xmlns:xlink="http://www.w3.org/1999/xlink"
        >
          <g
            id="Page-1"
            stroke="none"
            stroke-width="1"
            fill="none"
            fill-rule="evenodd"
          >
            <g
              id="WISION-Onboard-NFC-Leak"
              transform="translate(-142.000000, -170.000000)"
              :fill="leakDetection === 0 ? '#04F4FD' : '#E02020'"
              fill-rule="nonzero"
            >
              <g
                id="Icon-water-blue"
                transform="translate(142.000000, 170.000000)"
              >
                <path
                  id="Path"
                  class="animation"
                  d="M84.6923594,50.0126659 C84.2791761,46.6360509 83.1784831,43.4743552 81.5320866,40.6701293 L60.4997269,0 L39.4666024,40.6701293 L39.4681319,40.6701293 C37.8205337,43.4743552 36.7213701,46.6361603 36.3084054,50.0126659 L36.3084054,50.0140871 C36.2517047,50.4773768 36.2060382,50.9454766 36.1745742,51.4175118 C36.173154,51.4539147 36.1703135,51.4919574 36.1668175,51.5293441 C36.1372107,52.017777 36.119403,52.5073031 36.119403,53.0028417 C36.119403,66.4773808 47.0344544,77.4 60.4998361,77.4 C73.9648901,77.4 84.880597,66.4773808 84.880597,53.0028417 C84.880597,52.5074124 84.862243,52.0178863 84.8311068,51.5293441 C84.8283755,51.491848 84.8254258,51.454024 84.8226946,51.4175118 C84.7922138,50.9454766 84.7476398,50.4773768 84.6926871,50.0140871 L84.6926871,50.0126659 L84.6923594,50.0126659 Z"
                />
                <path
                  id="Path"
                  d="M31.1878325,80.0330956 C31.841363,79.5901531 32.0164655,78.6929548 31.5732507,78.0355983 C31.1311062,77.3770567 30.2453188,77.2018622 29.5896477,77.6441583 C26.7332081,79.5845503 25.2835821,81.758535 25.2835821,84.1068522 C25.2835821,92.6118655 43.4277748,97.2 60.501873,97.2 C77.5746869,97.2 95.7164179,92.6118655 95.7164179,84.1068522 C95.7164179,81.939871 94.4688662,79.9080028 92.0083337,78.0709389 C91.3763165,77.5985818 90.4801471,77.7326175 90.0095324,78.3702564 C89.5382755,79.0086496 89.6720641,79.9092958 90.3075063,80.3810064 C91.4732864,81.2532077 92.8613697,82.6046618 92.8613697,84.1066367 C92.8613697,88.9402158 79.5717757,94.3229747 60.5045488,94.3229747 C41.4332547,94.3229747 28.1441959,88.9402158 28.1441959,84.1066367 C28.144303,82.4463834 29.7993212,80.9742541 31.1878325,80.0330956"
                />
                <path
                  id="Path"
                  d="M106.269392,72.4108744 C105.523934,72.1118414 104.678305,72.4652343 104.370704,73.196455 C104.067245,73.9317662 104.422916,74.7696614 105.168155,75.0693403 C113.50106,78.4297479 118.087493,82.5157787 118.087493,86.578774 C118.087493,95.5341558 94.9470265,105.122588 60.5003265,105.122588 C26.0514464,105.122588 2.91021652,95.5342635 2.91021652,86.578774 C2.91021652,82.3868221 7.78146923,78.1947626 16.6265698,74.7773041 C17.3758423,74.4865596 17.7449196,73.6529701 17.453778,72.9127073 C17.1590394,72.1717987 16.310031,71.8100097 15.5670805,72.1000007 C5.38050518,76.0374838 0,81.0450487 0,86.5814651 C0,98.5934852 26.57149,108 60.5005445,108 C94.4270919,108 121,98.5934852 121,86.5814651 C121.002724,81.1947805 115.908891,76.2933519 106.269392,72.4108744"
                />
              </g>
            </g>
          </g>
        </svg>
      </div>
    </div>
    <div class="flex items-center justify-center text-2xl text-text-gray">
      <span v-if="humidity?.value">{{ humidity?.value }}{{ humidity?.unit }}</span>
      <span
        v-if="temp?.value && humidity?.value"
        class="px-2"
      >/</span>
      <span v-if="temp?.value">{{ temp?.value }}{{ temp?.unit }}</span>
    </div>

    <DeviceMeta
      v-if="props.meta"
      v-bind="props.meta"
    />
  </div>
</template>

<style scoped>
@keyframes animate {
 0% {transform: translateY(0px)}
 50% {transform: translateY(10px);}
}

 .animation{
  animation: animate 0.7s infinite;
 }
</style>
